import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Send, Loader2, Paperclip, Smile, MoreVertical, Check, CheckCheck, X, Trash2, Image as ImageIcon, Sparkles, Mic, Square, Play, Pause } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// ---- helpers -----------------------------------------------------------
const REACTIONS = ["👍", "🔥", "💪", "🎯", "❤️", "😅"];
const ATHLETE_COLOR = "#E2E30A"; // SPC yellow — athlete bubble accent
const COACH_COLOR = "#4C9AFF";   // TP blue — coach bubble accent

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    const sameDay = d.toDateString() === new Date().toDateString();
    if (sameDay) return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    if (diff < 7 * 86400) return d.toLocaleDateString(undefined, { weekday: "short" }) + " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return iso; }
}

// Central helper so every avatar/photo/voice tag uses the same URL builder
// that hits the new /messages/media endpoint (which knows about chat photos,
// voice notes AND profile avatars) instead of the tracker photo endpoint
// which is scoped to the caller's own food_photos records.
function fmtDayHeader(iso) {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  const d0 = new Date(d); d0.setHours(0, 0, 0, 0);
  if (d0.getTime() === today.getTime()) return "Today";
  if (d0.getTime() === yest.getTime()) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}

// Central helper so every avatar/photo/voice tag uses the same URL builder
// that hits the new /messages/media endpoint (which knows about chat photos,
// voice notes AND profile avatars) instead of the tracker photo endpoint
// which is scoped to the caller's own food_photos records.
//
// The auth token is stored under `spc_token` (not `token`) — see api.js and
// AuthContext.js. Getting this wrong meant every media URL had an empty
// auth query and the endpoint 401'd, which is why voice notes wouldn't
// play back after the Feb 19 fix.
export function mediaUrl(path) {
  if (!path) return "";
  const tok = localStorage.getItem("spc_token") || sessionStorage.getItem("spc_token") || "";
  return api.defaults.baseURL + "/messages/media/" + path + "?auth=" + encodeURIComponent(tok);
}

function Initials({ name, email, avatarPath, size = 32, bg }) {
  const src = (name || email || "?").trim();
  const letters = src.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("") || (src[0]?.toUpperCase() || "?");
  if (avatarPath) {
    return (
      <img data-testid={`avatar-${email || "u"}`}
        src={mediaUrl(avatarPath)}
        alt={src} className="shrink-0 object-cover"
        style={{ width: size, height: size, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.15)" }}
        onError={(e) => { e.currentTarget.style.display = "none"; }} />
    );
  }
  return (
    <span className="inline-flex items-center justify-center font-bold shrink-0"
      style={{ width: size, height: size, background: bg || "#1A1A1A", color: "#fff", fontSize: size * 0.4, border: "1px solid rgba(255,255,255,0.1)", borderRadius: "50%" }}
      aria-label={src}>{letters}</span>
  );
}

// Simple audio player bubble for voice notes. Uses the browser's native
// <audio> element under the hood + a lightweight play/pause chrome.
function VoicePlayer({ path, duration, accent }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const src = mediaUrl(path);
  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (playing) a.pause(); else a.play();
  };
  const pct = Math.min(100, progress * 100);
  return (
    <div className="flex items-center gap-2 min-w-[180px]">
      <button data-testid="voice-play-btn" onClick={toggle} type="button"
        className="h-8 w-8 flex items-center justify-center rounded-full shrink-0" style={{ background: accent, color: "#000" }}>
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
      </button>
      <div className="flex-1 h-1.5 bg-white/10 relative">
        <div className="h-full transition-[width] duration-100" style={{ width: `${pct}%`, background: accent }} />
      </div>
      <span className="text-[10px] font-mono text-zinc-300 tabular-nums">{Math.max(0, Math.round(duration || 0))}s</span>
      <audio ref={audioRef} src={src} preload="metadata"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setProgress(0); }}
        onError={() => { toast.error("Couldn't play voice note — try downloading it or ask them to re-record"); }}
        onTimeUpdate={(e) => setProgress(e.currentTarget.duration ? e.currentTarget.currentTime / e.currentTarget.duration : 0)} />
    </div>
  );
}

// ---- message bubble ---------------------------------------------------
function MessageBubble({ msg, isMine, showAvatar, showHeader, counterpart, onReact, onDelete, onEdit, isCoachAuthor }) {
  const [showActions, setShowActions] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(msg.text || "");
  const accent = isCoachAuthor ? COACH_COLOR : ATHLETE_COLOR;
  const bg = isMine ? "bg-[#1a1d0a] border-[#E2E30A]/30" : "bg-[#0F1420] border-[#4C9AFF]/25";
  const align = isMine ? "items-end" : "items-start";
  const cornerRadius = isMine ? "rounded-l-2xl rounded-tr-2xl" : "rounded-r-2xl rounded-tl-2xl";
  // Group reactions {emoji: count}
  const reactCounts = (msg.reactions || []).reduce((acc, r) => { acc[r.emoji] = (acc[r.emoji] || 0) + 1; return acc; }, {});
  if (msg.deleted) {
    return (
      <div className={`flex ${align} gap-2`}>
        <p className="text-[11px] italic text-zinc-500 px-3 py-1">Message deleted</p>
      </div>
    );
  }
  return (
    <div data-testid={`msg-${msg.id}`} className={`flex ${isMine ? "flex-row-reverse" : "flex-row"} gap-2 group`}>
      {!isMine && showAvatar ? (
        <Initials name={counterpart?.name} email={counterpart?.email} avatarPath={counterpart?.avatar_path || counterpart?.avatarPath} size={30} bg={COACH_COLOR + "22"} />
      ) : <span className="w-[30px] shrink-0" />}
      <div className={`flex flex-col ${align} max-w-[78%] min-w-0`}>
        {showHeader && !isMine && (
          <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: accent }}>{counterpart?.name || counterpart?.email}</p>
        )}
        <div className="relative">
          <div className={`${bg} ${cornerRadius} border px-3.5 py-2 shadow-lg ${editing ? "w-[280px]" : ""}`}
            onDoubleClick={() => !editing && setShowActions((v) => !v)}>
            {msg.photo_path && (
              <img data-testid={`msg-photo-${msg.id}`} src={mediaUrl(msg.photo_path)}
                alt="attachment" className="max-h-64 w-full object-contain mb-2 rounded" />
            )}
            {msg.voice_path && (
              <div data-testid={`msg-voice-${msg.id}`} className="mb-1">
                <VoicePlayer path={msg.voice_path} duration={msg.voice_duration_sec} accent={isCoachAuthor ? COACH_COLOR : ATHLETE_COLOR} />
              </div>
            )}
            {editing ? (
              <div className="flex flex-col gap-1.5">
                <Textarea autoFocus value={editText} onChange={(e) => setEditText(e.target.value)}
                  className="bg-black border-white/20 text-white text-sm min-h-[60px]" />
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => { setEditing(false); setEditText(msg.text || ""); }}
                    className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-white">Cancel</button>
                  <button data-testid={`msg-edit-save-${msg.id}`} onClick={() => { onEdit(msg.id, editText); setEditing(false); }}
                    className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>Save</button>
                </div>
              </div>
            ) : msg.text && (
              <p className="text-[15px] leading-snug whitespace-pre-wrap break-words text-white">{msg.text}</p>
            )}
            <div className={`flex items-center gap-1.5 mt-1 ${isMine ? "justify-end" : "justify-start"}`}>
              <span className="text-[10px] text-zinc-500 font-mono">{fmtWhen(msg.created_at)}</span>
              {msg.edited_at && <span className="text-[10px] italic text-zinc-500">edited</span>}
              {isMine && (
                (msg.read_by || []).some((e) => e !== msg.author_email)
                  ? <CheckCheck className="h-3 w-3 text-[#4C9AFF]" title="Seen" />
                  : <Check className="h-3 w-3 text-zinc-500" title="Sent" />
              )}
            </div>
          </div>
          {/* reactions row */}
          {Object.keys(reactCounts).length > 0 && (
            <div className={`flex flex-wrap gap-1 mt-1 ${isMine ? "justify-end" : "justify-start"}`}>
              {Object.entries(reactCounts).map(([em, count]) => (
                <button key={em} onClick={() => onReact(msg.id, em)}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-black/60 border border-white/10 text-[11px] hover:border-white/30 transition-colors">
                  <span>{em}</span>{count > 1 && <span className="text-[9px] text-zinc-400">{count}</span>}
                </button>
              ))}
            </div>
          )}
          {/* hover / long-press action bar */}
          <div className={`absolute ${isMine ? "left-0 -translate-x-full pr-1" : "right-0 translate-x-full pl-1"} top-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1`}>
            <button data-testid={`msg-react-btn-${msg.id}`} onClick={() => setShowActions((v) => !v)}
              className="p-1 bg-black/70 border border-white/10 text-zinc-300 hover:text-[#E2E30A]" title="React">
              <Smile className="h-3.5 w-3.5" />
            </button>
            {isMine && !editing && (
              <>
                <button data-testid={`msg-edit-btn-${msg.id}`} onClick={() => setEditing(true)}
                  className="p-1 bg-black/70 border border-white/10 text-zinc-300 hover:text-[#4C9AFF]" title="Edit">
                  <Sparkles className="h-3.5 w-3.5" />
                </button>
                <button data-testid={`msg-delete-btn-${msg.id}`} onClick={() => onDelete(msg.id)}
                  className="p-1 bg-black/70 border border-white/10 text-zinc-300 hover:text-[#FF3B30]" title="Delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
          {showActions && (
            <div className={`absolute z-30 ${isMine ? "right-0" : "left-0"} -top-10 flex items-center gap-1 bg-[#0F0F0F] border border-white/15 px-1.5 py-1 shadow-2xl`}>
              {REACTIONS.map((em) => (
                <button key={em} onClick={() => { onReact(msg.id, em); setShowActions(false); }}
                  className="text-lg hover:scale-125 transition-transform">{em}</button>
              ))}
              <button onClick={() => setShowActions(false)} className="p-0.5 text-zinc-400"><X className="h-3 w-3" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- main chat panel --------------------------------------------------
export default function ChatPanel({ athleteEmail, quickReplies, emptyStateTitle = "Start the conversation", emptyStateHint = "Share how you're feeling, ask a question, or send a photo." }) {
  const { email: myEmail } = useAuth();
  const [messages, setMessages] = useState([]);
  const [counterpart, setCounterpart] = useState(null);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [pendingPhoto, setPendingPhoto] = useState(null); // {path, previewUrl}
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recTimer, setRecTimer] = useState(0);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const lastIdRef = useRef(null);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTsRef = useRef(0);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = athleteEmail ? `?athlete_email=${encodeURIComponent(athleteEmail)}` : "";
      const { data } = await api.get(`/messages/thread${params}`);
      setMessages(data.messages || []);
      setThread(data.thread);
      setCounterpart(data.counterpart);
      // mark thread as read every load to clear unread badges snappy
      await api.post(`/messages/read-thread${params}`).catch(() => {});
    } catch (e) { if (!silent) toast.error(apiError(e, "Could not load messages")); }
    finally { setLoading(false); }
  }, [athleteEmail]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [athleteEmail]);

  // Poll every 15s while the panel is mounted so new messages appear even
  // if the user doesn't refresh. Keeps traffic tiny (one indexed query).
  useEffect(() => {
    const id = setInterval(() => load(true), 15000);
    return () => clearInterval(id);
  }, [load]);

  // Auto-scroll to newest message when the list changes and the last id shifted.
  useEffect(() => {
    if (!messages.length) return;
    const lastId = messages[messages.length - 1]?.id;
    if (lastIdRef.current !== lastId) {
      lastIdRef.current = lastId;
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      });
    }
  }, [messages]);

  // Group messages by day + collapse consecutive-same-author metadata so the
  // thread reads like a chat and not a wall of timestamps.
  const grouped = useMemo(() => {
    const groups = [];
    let currentDay = null;
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      const dayKey = (m.created_at || "").slice(0, 10);
      if (dayKey !== currentDay) {
        groups.push({ type: "day", key: dayKey, label: fmtDayHeader(m.created_at) });
        currentDay = dayKey;
      }
      const prev = messages[i - 1];
      const showAvatar = !prev || prev.author_email !== m.author_email
        || (new Date(m.created_at) - new Date(prev.created_at)) > 5 * 60 * 1000
        || (prev.created_at || "").slice(0, 10) !== dayKey;
      groups.push({ type: "msg", msg: m, showAvatar, showHeader: showAvatar });
    }
    return groups;
  }, [messages]);

  const sendMessage = async ({ textOverride, photoPath } = {}) => {
    const body = (textOverride ?? text).trim();
    if (!body && !photoPath && !pendingPhoto?.path) return;
    setSending(true);
    try {
      const payload = { text: body, photo_path: photoPath || pendingPhoto?.path || null };
      if (athleteEmail) payload.athlete_email = athleteEmail;
      await api.post("/messages/thread", payload);
      setText("");
      setPendingPhoto(null);
      load(true);
    } catch (e) { toast.error(apiError(e, "Couldn't send")); }
    finally { setSending(false); }
  };

  const react = async (id, emoji) => {
    try { await api.post(`/messages/${id}/reaction`, { emoji }); load(true); }
    catch (e) { toast.error(apiError(e, "Couldn't react")); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/messages/${id}`); load(true); }
    catch (e) { toast.error(apiError(e, "Couldn't delete")); }
  };
  const edit = async (id, newText) => {
    try { await api.patch(`/messages/${id}`, { text: newText }); load(true); }
    catch (e) { toast.error(apiError(e, "Couldn't edit")); }
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Photo too large (8MB max)"); return; }
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/messages/photo", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingPhoto({ path: data.photo_path, previewUrl: URL.createObjectURL(f) });
    } catch (e) { toast.error(apiError(e, "Photo upload failed")); }
    finally { setUploadingPhoto(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // Voice-note recording — MediaRecorder in webm/opus which every modern
  // Chromium/Safari/Firefox supports. Auto-stops at 30s; tap the stop
  // button to send early. Uploads then dispatches a message with the
  // resulting voice_path so the receiver sees an inline playback bubble.
  const startRecording = async () => {
    if (recording) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice notes aren't supported on this browser"); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      // Pick the file extension from whatever the browser actually recorded
      // — iOS Safari captures mp4/aac, everything else webm/opus. Using
      // the wrong extension makes some browsers refuse to play the file
      // back even though the content-type is right.
      const chosenType = (mime || "audio/webm").split(";")[0];
      const ext = chosenType.includes("mp4") ? "mp4" : chosenType.includes("ogg") ? "ogg" : "webm";
      mediaRecRef.current = rec;
      mediaRecRef.current._ext = ext;
      mediaRecRef.current._ctype = chosenType;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const durationSec = (Date.now() - startTsRef.current) / 1000;
        setRecording(false); setRecTimer(0);
        if (durationSec < 0.6) { toast.error("Too short — hold the mic and speak"); return; }
        const blob = new Blob(chunksRef.current, { type: rec._ctype || rec.mimeType || "audio/webm" });
        setUploadingVoice(true);
        try {
          const form = new FormData();
          const ext = rec._ext || "webm";
          const ctype = rec._ctype || rec.mimeType || "audio/webm";
          form.append("file", new File([blob], `voice.${ext}`, { type: ctype }));
          const { data } = await api.post("/messages/voice", form, { headers: { "Content-Type": "multipart/form-data" } });
          const payload = { text: "", voice_path: data.voice_path, voice_duration_sec: durationSec };
          if (athleteEmail) payload.athlete_email = athleteEmail;
          await api.post("/messages/thread", payload);
          load(true);
        } catch (e) { toast.error(apiError(e, "Voice send failed")); }
        finally { setUploadingVoice(false); }
      };
      startTsRef.current = Date.now();
      rec.start();
      setRecording(true);
      setRecTimer(0);
      timerRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - startTsRef.current) / 1000);
        setRecTimer(secs);
        if (secs >= 30) stopRecording();
      }, 200);
    } catch (e) { toast.error("Microphone permission denied — enable it in your browser"); }
  };
  const stopRecording = () => {
    try { mediaRecRef.current?.state === "recording" && mediaRecRef.current.stop(); }
    catch { /* noop */ }
  };
  const cancelRecording = () => {
    try { mediaRecRef.current && (mediaRecRef.current.onstop = null, mediaRecRef.current.state === "recording" && mediaRecRef.current.stop()); } catch { /* noop */ }
    chunksRef.current = [];
    clearInterval(timerRef.current);
    setRecording(false); setRecTimer(0);
  };
  useEffect(() => () => { try { mediaRecRef.current?.state === "recording" && mediaRecRef.current.stop(); } catch {}; clearInterval(timerRef.current); }, []);

  // If athlete has no coach assigned
  if (!loading && !thread) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center">
        <Initials name={myEmail} size={64} bg="#1a1a1a" />
        <p className="mt-4 font-display uppercase tracking-tight text-lg text-white">No coach yet</p>
        <p className="text-sm text-zinc-400 mt-1 max-w-xs">Ask your admin to link you to a coach — messages will unlock the moment they do.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-black/60 backdrop-blur-md border-b border-white/10 sticky top-0 z-10">
        <Initials name={counterpart?.name} email={counterpart?.email} avatarPath={counterpart?.avatar_path || counterpart?.avatarPath} size={40} bg={COACH_COLOR + "22"} />
        <div className="min-w-0 flex-1">
          <p className="font-display uppercase tracking-tight text-white text-base truncate">{counterpart?.name || counterpart?.email?.split("@")[0] || "Coach"}</p>
          <p className="text-[10px] font-mono text-zinc-400 truncate">{counterpart?.role === "athlete" ? "Athlete" : "Coach"} · {counterpart?.email}</p>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} data-testid="chat-scroll" className="flex-1 overflow-y-auto px-3 py-4 space-y-2 bg-[radial-gradient(ellipse_at_top,rgba(76,154,255,0.06),transparent_60%)]">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 max-w-sm mx-auto">
            <div className="inline-flex items-center justify-center h-14 w-14 border border-[#E2E30A]/40 bg-[#E2E30A]/[0.06] mx-auto mb-3">
              <Sparkles className="h-6 w-6 text-[#E2E30A]" />
            </div>
            <p className="font-display uppercase tracking-tight text-white text-lg">{emptyStateTitle}</p>
            <p className="text-sm text-zinc-400 mt-1">{emptyStateHint}</p>
          </div>
        ) : grouped.map((g, i) => {
          if (g.type === "day") return (
            <div key={"day-" + g.key} className="flex items-center gap-3 py-3">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{g.label}</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
          );
          const m = g.msg;
          const isMine = m.author_email === (myEmail || "").toLowerCase();
          const isCoachAuthor = m.author_role === "coach" || m.author_role === "admin";
          return <MessageBubble key={m.id} msg={m} isMine={isMine} showAvatar={g.showAvatar} showHeader={g.showHeader}
            counterpart={counterpart} onReact={react} onDelete={del} onEdit={edit} isCoachAuthor={isCoachAuthor} />;
        })}
      </div>

      {/* Quick replies */}
      {(quickReplies || []).length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto px-3 py-2 border-t border-white/5 bg-black/40" data-swipe-ignore>
          {quickReplies.map((q) => (
            <button key={q} data-testid={`chat-quick-${q.slice(0,10)}`}
              onClick={() => sendMessage({ textOverride: q })} disabled={sending}
              className="shrink-0 px-3 py-1.5 border border-white/15 text-white text-xs bg-[#0F0F0F] hover:border-[#E2E30A]/60 hover:text-[#E2E30A] transition-colors">
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Pending photo preview */}
      {pendingPhoto && (
        <div className="border-t border-white/10 px-3 py-2 bg-black/70 flex items-center gap-3">
          <img src={pendingPhoto.previewUrl} alt="preview" className="h-14 w-14 object-cover border border-white/15" />
          <p className="text-xs text-zinc-300 flex-1">Photo attached — hit send.</p>
          <button onClick={() => setPendingPhoto(null)} className="p-1 text-zinc-400 hover:text-[#FF3B30]"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Recording bar */}
      {recording && (
        <div data-testid="chat-recording-bar" className="border-t border-[#FF3B30]/40 bg-[#FF3B30]/[0.08] px-3 py-2 flex items-center gap-3">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF3B30] animate-pulse" />
          <p className="text-xs uppercase tracking-wider text-[#FF3B30] font-bold">Recording · {recTimer}s / 30s</p>
          <button data-testid="voice-cancel-btn" onClick={cancelRecording}
            className="ml-auto h-8 px-2 border border-white/15 text-zinc-300 hover:text-white text-[10px] uppercase">Cancel</button>
          <button data-testid="voice-stop-btn" onClick={stopRecording}
            className="h-8 px-3 bg-[#FF3B30] text-white text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1">
            <Square className="h-3 w-3" /> Send
          </button>
        </div>
      )}

      {/* Composer */}
      <div className="border-t border-white/10 bg-[#0A0A0A] px-2 py-2 flex items-end gap-2">
        <button data-testid="chat-attach-btn" onClick={() => fileRef.current?.click()} disabled={uploadingPhoto || sending || recording}
          title="Attach photo"
          className="h-11 w-11 flex items-center justify-center border border-white/15 text-zinc-300 hover:text-[#E2E30A] hover:border-[#E2E30A]/60 transition-colors disabled:opacity-50 shrink-0">
          {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </button>
        <button data-testid="chat-voice-btn" onClick={recording ? stopRecording : startRecording} disabled={uploadingVoice || sending}
          title={recording ? "Stop & send" : "Record voice note (up to 30s)"}
          className={`h-11 w-11 flex items-center justify-center border transition-colors disabled:opacity-50 shrink-0 ${recording ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-white/15 text-zinc-300 hover:text-[#FF3B30] hover:border-[#FF3B30]/60"}`}>
          {uploadingVoice ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFilePick} />
        <Textarea data-testid="chat-input" value={text}
          onChange={(e) => {
            setText(e.target.value);
            const el = e.target;
            el.style.height = "auto";
            el.style.height = Math.min(el.scrollHeight, 240) + "px";
          }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type a message… Enter to send · Shift+Enter for new line"
          className="flex-1 bg-black border-white/15 text-white text-base leading-relaxed min-h-[60px] max-h-60 resize-none py-3 px-3 whitespace-pre-wrap break-words"
          rows={2} />
        <button data-testid="chat-send-btn" onClick={() => sendMessage()} disabled={sending || (!text.trim() && !pendingPhoto)}
          className="h-11 px-4 bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-bold text-xs uppercase tracking-wider disabled:opacity-50 inline-flex items-center gap-1 shrink-0">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /></>}
        </button>
      </div>
    </div>
  );
}

// Quick-reply chip presets — exposed so both /messages and CoachDashboard
// can pass the right set for their side.
export const COACH_QUICK_REPLIES = [
  "Great work today 💪",
  "Ease up — recovery first",
  "Push a bit harder 🎯",
  "Session moved to tomorrow",
  "How did that feel?",
];
export const ATHLETE_QUICK_REPLIES = [
  "Feeling strong 💪",
  "Legs feel heavy today",
  "Slept badly, easing off",
  "Nailed the fuel plan 🎯",
  "Need advice",
  "Thanks coach!",
];
